import React, { useCallback, useEffect, useState } from "react";
import { esIOS, usePush, instruccionesBloqueadas } from "../utils/pushNotificaciones";
import { permisoDesactivado, setPermisoDesactivado } from "../utils/permisosApp";

// PERMISOS dentro de los avisos (campana 🔔): notificaciones, ubicación y cámara (fichajes).
// Sin tarjeta propia en el panel ni botones de «probar»: solo el estado y, si hace falta, «Permitir».

const COLOR = { ok: "#15803d", falta: "#b45309", mal: "#b91c1c", gris: "#64748b" };

// Estado de un permiso del navegador (geolocation / camera): "granted" | "prompt" | "denied" | "desconocido" (p. ej. Safari no lo informa)
function usePermiso(nombre) {
  const [estado, setEstado] = useState("desconocido");
  const comprobar = useCallback(async () => {
    try {
      if (!navigator.permissions?.query) { setEstado("desconocido"); return null; }
      const r = await navigator.permissions.query({ name: nombre });
      setEstado(r.state);
      r.onchange = () => setEstado(r.state);
      return r;
    } catch (e) { setEstado("desconocido"); return null; }
  }, [nombre]);
  useEffect(() => {
    let r = null, vivo = true;
    comprobar().then((x) => { if (!vivo && x) x.onchange = null; r = x; });
    const alVolver = () => { if (document.visibilityState === "visible") comprobar(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => { vivo = false; if (r) r.onchange = null; document.removeEventListener("visibilitychange", alVolver); };
  }, [comprobar]);
  return [estado, comprobar];
}

const ayudaBloqueado = (que) => (esIOS()
  ? `En iPhone: Ajustes › Safari › ${que} › elige «Preguntar» o «Permitir». O toca «aA» en la barra de direcciones › Ajustes del sitio web › ${que} › Permitir.`
  : `Toca el candado (o los ajustes ⋮) junto a la dirección › Permisos › ${que} › Permitir.`);

const CHIP = {
  granted: { t: "✅ Permitida", c: COLOR.ok, bg: "#dcfce7" },
  prompt: { t: "❓ Sin decidir", c: COLOR.falta, bg: "#fef3c7" },
  denied: { t: "⛔ Bloqueada", c: COLOR.mal, bg: "#fee2e2" },
  desconocido: { t: "❔ Sin comprobar", c: COLOR.gris, bg: "#f1f5f9" },
};

function Fila({ icono, titulo, para, estado, chipTxt, permitir, desactivar, ocupado, ayuda }) {
  const chip = chipTxt ? { ...(CHIP[estado] || CHIP.desconocido), t: chipTxt } : (CHIP[estado] || CHIP.desconocido);
  const btn = (txt, onClick, color, borde) => (
    <button onClick={onClick} disabled={ocupado} style={{ marginTop: 8, width: "100%", border: borde ? `1px solid ${borde}` : "none", borderRadius: 10, padding: "9px 0", background: borde ? "#fff" : color, color: borde ? color : "#fff", fontWeight: 800, fontSize: 14.5, cursor: "pointer", opacity: ocupado ? 0.6 : 1 }}>{ocupado ? "…" : txt}</button>
  );
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "10px 12px", marginTop: 8, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 24 }}>{icono}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 15.5, color: "#1e293b" }}>{titulo}</div>
          <div style={{ fontSize: 12.5, color: "#64748b" }}>{para}</div>
        </div>
        <span style={{ background: chip.bg, color: chip.c, borderRadius: 999, padding: "3px 9px", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>{chip.t}</span>
      </div>
      {permitir && btn("Activar", permitir, "#ea580c")}
      {desactivar && btn("Desactivar", desactivar, "#b91c1c", "#fca5a5")}
      {ayuda && <div style={{ marginTop: 8, fontSize: 13, color: "#7c2d12", background: "#fff7ed", borderRadius: 8, padding: "7px 9px", whiteSpace: "pre-line" }}>{ayuda}</div>}
    </div>
  );
}

export default function Permisos({ user }) {
  const push = usePush(user);
  const [ubic, recompUbic] = usePermiso("geolocation");
  const [cam, recompCam] = usePermiso("camera");
  const [abierto, setAbierto] = useState(null);       // null = automático (plegado si todo está activado)
  const [confirmando, setConfirmando] = useState(false);
  const [quitando, setQuitando] = useState(false);
  const [confirmaPerm, setConfirmaPerm] = useState(null);   // "ubicacion" | "camara"
  const [, forzar] = useState(0);
  useEffect(() => {
    const h = () => forzar((n) => n + 1);
    window.addEventListener("permisos:cambio", h);
    return () => window.removeEventListener("permisos:cambio", h);
  }, []);
  const ubicOff = permisoDesactivado("ubicacion"), camOff = permisoDesactivado("camara");

  const permitirUbicacion = () => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(() => recompUbic(), () => recompUbic(), { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };
  const permitirCamara = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      s.getTracks().forEach((t) => t.stop());
    } catch (e) { /* denegada o sin cámara: el estado se vuelve a leer abajo */ }
    recompCam();
  };
  const desactivarNotif = async () => { setQuitando(true); await push.desactivar(); setQuitando(false); setConfirmando(false); };
  // ubicación/cámara: interruptor de la app en este dispositivo (el navegador no deja quitarse el permiso a sí mismo)
  const activarPerm = (k) => { setPermisoDesactivado(k, false); if (k === "ubicacion") permitirUbicacion(); else permitirCamara(); };

  if (!user) return null;
  const nEstado = { activo: "granted", desactivado: "desconocido", bloqueado: "denied" }[push.estado] || "prompt";
  const nChip = { activo: "✅ Activadas", desactivado: "🔕 Desactivadas", bloqueado: "⛔ Bloqueadas", instalar: "📲 Instala la app", no_soportado: "❔ No disponible" }[push.estado];
  const todoOk = push.estado === "activo" && ubic === "granted" && cam === "granted" && !ubicOff && !camOff;
  const desplegado = abierto === null ? !todoOk : abierto;
  return (
    <div style={{ marginBottom: 12, borderTop: "1px solid #e2e8f0", paddingTop: 10 }}>
      <button onClick={() => setAbierto(!desplegado)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, border: "none", background: "transparent", cursor: "pointer", padding: 0, textAlign: "left" }}>
        <span style={{ flex: 1, fontWeight: 900, fontSize: 15, color: "#1e3a5f" }}>🔐 Permisos</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: todoOk ? COLOR.ok : COLOR.falta }}>{todoOk ? "✅ Todo activado" : "⚠️ Falta algo"}</span>
        <span style={{ transition: "transform .2s", transform: desplegado ? "rotate(180deg)" : "none", color: "#64748b" }}>▾</span>
      </button>
      {desplegado && (
        <>
          {push.estado !== "no_soportado" && push.estado !== "cargando" && (
            <Fila icono="🔔" titulo="Notificaciones" para="Avisos al móvil cuando cambie algo de tus pisos" estado={nEstado} chipTxt={nChip}
              permitir={["desactivado", "bloqueado", "inactivo"].includes(push.estado) ? push.activar : null}
              desactivar={push.estado === "activo" ? () => setConfirmando(true) : null}
              ocupado={push.activando}
              ayuda={push.estado === "bloqueado" || push.estado === "instalar" ? instruccionesBloqueadas() : null} />
          )}
          <Fila icono="📍" titulo="Ubicación" para="Para comprobar que estás en el piso al fichar" estado={ubicOff ? "desconocido" : ubic}
            chipTxt={ubicOff ? "🔕 Desactivada" : null}
            permitir={ubicOff ? () => activarPerm("ubicacion") : ubic === "prompt" ? permitirUbicacion : null}
            desactivar={!ubicOff && ubic === "granted" ? () => setConfirmaPerm("ubicacion") : null}
            ayuda={!ubicOff && ubic === "denied" ? ayudaBloqueado("Ubicación") : null} />
          <Fila icono="📷" titulo="Cámara" para="Para leer el QR del piso al fichar" estado={camOff ? "desconocido" : cam}
            chipTxt={camOff ? "🔕 Desactivada" : null}
            permitir={camOff ? () => activarPerm("camara") : cam === "prompt" ? permitirCamara : null}
            desactivar={!camOff && cam === "granted" ? () => setConfirmaPerm("camara") : null}
            ayuda={!camOff && cam === "denied" ? ayudaBloqueado("Cámara") : null} />
        </>
      )}
      {(confirmando || confirmaPerm) && (
        <div onClick={() => { if (!quitando) { setConfirmando(false); setConfirmaPerm(null); } }} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 5000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, border: "4px solid #dc2626", width: "min(420px, 100%)", padding: "18px 16px 14px", textAlign: "center", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}>
            <div style={{ fontSize: 44 }}>⚠️</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#b91c1c", marginTop: 4 }}>NO RECOMENDADO</div>
            <div style={{ fontSize: 15.5, color: "#7f1d1d", fontWeight: 700, margin: "10px 0 16px", lineHeight: 1.4 }}>
              {confirmaPerm
                ? <>Si desactivas {confirmaPerm === "camara" ? "la cámara" : "la ubicación"} <u>no podrás fichar</u> hasta que la vuelvas a activar.</>
                : <>Si las desactivas <u>no vas a recibir ningún aviso</u> en el móvil cuando cambie algo de tus pisos (horarios, pisos nuevos, cambios de ruta…).</>}
            </div>
            <button onClick={() => { setConfirmando(false); setConfirmaPerm(null); }} disabled={quitando} style={{ width: "100%", border: "none", borderRadius: 12, padding: "13px 0", background: "#16a34a", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", marginBottom: 8 }}>{confirmaPerm ? "Mantener activada" : "Mantener activadas"}</button>
            <button onClick={confirmaPerm ? () => { setPermisoDesactivado(confirmaPerm, true); setConfirmaPerm(null); } : desactivarNotif} disabled={quitando} style={{ width: "100%", border: "2px solid #dc2626", borderRadius: 12, padding: "10px 0", background: "#fff", color: "#dc2626", fontWeight: 800, fontSize: 14, cursor: "pointer", opacity: quitando ? 0.6 : 1 }}>{quitando ? "Desactivando…" : "Sí, desactivar"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
